import { useState } from "react";
import { createPortal } from "react-dom";
import ScrollAnimation from "react-animate-on-scroll";
import { CertificatePreview, Container } from "./styles";

// The certificate image lives in public/Images/aws-certificate.png.
// If it ever goes missing, the hover preview simply stays hidden.
const certificateImage = `${process.env.PUBLIC_URL}/Images/aws-certificate.png`;
const certificateLink =
  "https://www.coursera.org/account/accomplishments/verify/9484264NTT5D";

const experience = [
  {
    role: "Full Stack Developer",
    company: "Timeflow System Group AB",
    location: "Remote, Sweden",
    period: "Jun 2026 - Sep 2026",
    points: [
      "Built 50+ production screens across Customer, Professional and Team dashboards using Claude Code, Next.js 15, TypeScript and Tailwind CSS in an AI native SDLC.",
      "Worked in a Turborepo monorepo with Jira, Bitbucket, Confluence and Rovo AI, delivering consistent daily PRs.",
    ],
  },
  {
    role: "Full Stack JavaScript Developer",
    company: "Onlyoffice",
    location: "Remote, Latvia",
    period: "Feb 2025 - Apr 2026",
    points: [
      "Developed and maintained responsive UI components across multiple OnlyOffice web pages, ensuring consistency and cross browser compatibility.",
      "Built and integrated custom macros and plugins using JavaScript to extend OnlyOffice document editor functionality and automate repetitive workflows.",
    ],
  },
  {
    role: "Full Stack Engineer",
    company: "Checkroom",
    location: "Remote, Poland",
    period: "Dec 2023 - Dec 2024",
    points: [
      "Designed and launched a comprehensive full stack web platform using Next.js, handling both frontend architecture and backend systems end to end.",
      "Engineered a multi functional client facing web app with user authentication, dynamic data handling and seamless frontend to backend communication.",
    ],
  },
  {
    role: "Junior Software Developer",
    company: "Cryptigo LLC",
    location: "Remote, UAE",
    period: "Jun 2023 - Oct 2023",
    points: [
      "Implemented frontend features in Next.js and Tailwind CSS and wrote Jest unit tests for a live blockchain web app.",
    ],
  },
];

const languages = [
  { name: "English", level: "Full Professional Proficiency" },
  { name: "Swedish", level: "Conversational Proficiency" },
  { name: "Arabic", level: "Elementary Proficiency" },
];

export function Experience() {
  const [certificateReady, setCertificateReady] = useState(false);
  const [certificateOpen, setCertificateOpen] = useState(false);

  return (
    <Container id="experience">
      <ScrollAnimation animateIn="fadeInLeft">
        <h2>Experience</h2>
      </ScrollAnimation>

      <div className="timeline">
        {experience.map((job, index) => (
          <ScrollAnimation
            key={job.company}
            animateIn="fadeInUp"
            delay={0.1 * index * 1000}
          >
            <article className="job">
              <span className="dot" />
              <header>
                <h3>{job.role}</h3>
                <span className="period">{job.period}</span>
              </header>
              <p className="company">
                {job.company} <span className="location">{job.location}</span>
              </p>
              <ul>
                {job.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </article>
          </ScrollAnimation>
        ))}
      </div>

      <div className="extras">
        <ScrollAnimation animateIn="fadeInUp">
          <article className="card">
            <h4>Education</h4>
            <h3>Bachelor's in Computer Science</h3>
            <p>Federal Urdu University of Arts, Sciences and Technology</p>
            <span className="period">Feb 2022 - Aug 2026</span>
          </article>
        </ScrollAnimation>

        <ScrollAnimation animateIn="fadeInUp" delay={0.1 * 1000}>
          <article
            className="card cert"
            tabIndex={0}
            onMouseEnter={() => setCertificateOpen(true)}
            onMouseLeave={() => setCertificateOpen(false)}
            onFocus={() => setCertificateOpen(true)}
            onBlur={() => setCertificateOpen(false)}
          >
            <h4>Certification</h4>
            <h3>AWS Cloud Technical Essentials</h3>
            <p>Learned EC2, DynamoDB, S3, RDS, Lambda and Networking in AWS</p>
            <span className="period">Dec 2024 - Feb 2025</span>
            <a
              className="verify"
              href={certificateLink}
              target="_blank"
              rel="noreferrer"
            >
              Verify certificate ↗
            </a>
            {certificateReady && (
              <span className="hint">Hover to preview the certificate</span>
            )}
            {createPortal(
              <CertificatePreview $open={certificateOpen && certificateReady}>
                <img
                  src={certificateImage}
                  alt="AWS Cloud Technical Essentials certificate"
                  onLoad={() => setCertificateReady(true)}
                  onError={() => setCertificateReady(false)}
                />
              </CertificatePreview>,
              document.body
            )}
          </article>
        </ScrollAnimation>

        <ScrollAnimation animateIn="fadeInUp" delay={0.2 * 1000}>
          <article className="card">
            <h4>Languages</h4>
            <ul className="languages">
              {languages.map((language) => (
                <li key={language.name}>
                  <strong>{language.name}</strong>
                  <span>{language.level}</span>
                </li>
              ))}
            </ul>
          </article>
        </ScrollAnimation>
      </div>
    </Container>
  );
}
